import { useState } from "react";

import { find, isEmpty } from "lodash/fp";

import Typography from "@mui/material/Typography";

import { isPaidPlan } from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useWebhooks from "@app/analytics/hooks/integrations/useWebhooks";
import IntegrationDetailHeader from "@app/analytics/Integrations/components/IntegrationDetailHeader";
import IntegrationError from "@app/analytics/Integrations/components/IntegrationError";
import IntegrationNotConnected from "@app/analytics/Integrations/components/IntegrationNotConnected";
import IntegrationUpsell from "@app/analytics/Integrations/components/IntegrationUpsell";
import { PROVIDER_CONTENT } from "@app/analytics/Integrations/constants";
import {
  IntegrationDetailContent,
  LoadingIntegrationDetail,
} from "@app/analytics/Integrations/styles";

import AddOrEditWebhookModal from "./AddOrEditWebhookModal";
import WebhooksTable from "./WebhooksTable";

const WebhooksIntegrationConfig = () => {
  const { currentPlan, plansInitialLoading } = useBillingContext();
  const { userIntegrationsByCategory, isLoading: isIntegrationsLoading } =
    useIntegrations();

  const integration = find(
    { providerId: "webhooks" },
    userIntegrationsByCategory.workflow
  );

  const { webhooks, isLoading: isWebhooksLoading } = useWebhooks({
    enabled: !(integration?.isDisabled ?? true),
  });

  const [addingWebhook, setAddingWebhook] = useState(false);

  if (!plansInitialLoading && !isPaidPlan(currentPlan.type)) {
    return <IntegrationUpsell type="premium" />;
  }

  const isLoading =
    plansInitialLoading || isIntegrationsLoading || isWebhooksLoading;

  if (isLoading) {
    return <LoadingIntegrationDetail />;
  }

  if (!integration) {
    return <IntegrationError />;
  }

  const hasWebhooks = !isWebhooksLoading && !isEmpty(webhooks);
  const providerContent = PROVIDER_CONTENT[integration.providerId];

  const connectCta = {
    label: "Add webhook",
    onClick: () => setAddingWebhook(true),
    show: true,
  };

  const handleCloseModal = () => setAddingWebhook(false);

  return (
    <>
      <IntegrationDetailHeader
        cta={connectCta}
        provider={integration}
        documentationUrl={integration.documentationUrl}
      >
        <Typography variant="bodySecondaryReg" color="shades.neutral.600">
          {providerContent.headerDescription ??
            providerContent.shortDescription}
        </Typography>
      </IntegrationDetailHeader>
      {integration.isDisabled ? (
        <IntegrationNotConnected
          cta={connectCta}
          heading="Webhooks are disabled"
          provider={integration}
        />
      ) : hasWebhooks ? (
        <IntegrationDetailContent spacing={1.5}>
          <Typography variant="bodySecondaryMed" color="shades.neutral.800">
            Configuration
          </Typography>
          <WebhooksTable webhooks={webhooks} />
        </IntegrationDetailContent>
      ) : (
        <IntegrationNotConnected
          cta={connectCta}
          heading="No Webhooks connected"
          provider={integration}
        />
      )}
      {!integration.isDisabled && (
        <AddOrEditWebhookModal
          onClose={handleCloseModal}
          open={addingWebhook}
        />
      )}
    </>
  );
};

export default WebhooksIntegrationConfig;
